import { LOCALES, type Locale } from '../constants'
import { isDayPillar } from '../teaser'
import type { ChartPillars } from '../chartFacts'
import { periodScore, monthPillarsOfYear } from '../yearFlow'
import type { Relation } from '../ganzhi'
import type { AgeBand } from './ageBand'
import { isMarried, isYouthAge } from '../viewer'
import type { Concern, RelationshipStatus } from '../birthForm'
import { sha256Hex } from '../hash'
import { baseMonth, baseMonthStarted } from '../baseMonth'

/**
 * 한 페이지 리포트의 섹션 명세. 서버·클라이언트 공용.
 *
 * 결과를 만드는 순간 화면이 AI 섹션을 두 번에 나눠 모두 요청한다 (1차 6개 동시 → 2차 6개 동시).
 * 2차 섹션은 서버가 1차 섹션 캐시에서 핵심 문장을 꺼내 "이미 쓴 말" 로 넘겨 반복을 막는다.
 *
 * 서버로 가는 값: 네 기둥 간지, 대운 간지와 시작 나이, 기준 날짜(월별 흐름 시작 달), 연애 상태 묶음, 언어.
 * 이름·생년월일·성별·도시는 보내지 않는다. 캐시 키는 섹션별 재료의 SHA-256 이라 키에서 사주를 읽을 수 없다.
 */

/**
 * v15 : 한국어가 아닌 재료에 조사 "에" 가 그대로 붙어 나가던 것을 고쳤다
 * (`placeList()`, `facts.ts`). 프롬프트 글자가 바뀌었으므로 올린다.
 * 같이 고친 guard 오탐('망하' 가 희망하·소망하를 잡던 것)은 검사기만 바뀌어 버전과 무관하다.
 */
// v16 : 성별 줄·글자 규칙·다섯 기운 수·장마다 다른 10년 흐름 줄·꼬리표/점수 → 말·계절 빼기·20살 아래 장
// v17 : 재료를 사람 말로 — 자리 이름·겉/바탕·개수·기운으로 부르는 사람 빼기, 달 주제 하나, 공통 성격 재료는 평생 장에만.
//   이미 산 리포트는 결과에 고정된 섹션(result_sections)이 먼저 읽히므로 다시 만들어지지 않는다 — 새로 만드는 섹션부터 새 재료.
export const REPORT_VERSION = 'v42'

/** 탭 순서 (15개). ai 가 아닌 탭은 규칙 계산·다른 화면 연결이다. */
export const REPORT_TABS = [
  'today', 'life', 'wealth', 'career', 'business', 'love', 'marriage', 'friends',
  'network', 'daeun', 'monthly', 'condition', 'clash', 'goodDays', 'compat',
] as const

export const AI_SECTIONS = ['life', 'wealth', 'career', 'business', 'love', 'marriage', 'friends', 'network', 'daeun', 'monthly', 'condition', 'clash'] as const
export type AiSection = (typeof AI_SECTIONS)[number]

export function isAiSection(v: unknown): v is AiSection {
  return typeof v === 'string' && (AI_SECTIONS as readonly string[]).includes(v)
}

/** 2차 섹션이 반복을 피해야 하는 1차 섹션 */
export const AVOID_SOURCES: Record<AiSection, readonly AiSection[]> = {
  life: [],
  wealth: [],
  love: [],
  friends: [],
  condition: [],
  monthly: [],
  career: ['life', 'wealth'],
  business: ['wealth', 'life'],
  marriage: ['love', 'life'],
  network: ['friends', 'life'],
  clash: ['friends', 'love'],
  daeun: ['life', 'monthly'],
}

/**
 * 섹션마다 고유한 구역 (소제목). 모든 섹션이 같은 틀이면 복사한 느낌이 나서 섹션별로 따로 정했다.
 * 소제목 문구는 화면 문구 part_<섹션>_<구역> (애정·결혼은 연애 상태별 문구가 따로 있는 구역이 있다).
 */
export const SECTION_PARTS = {
  life: ['nature', 'flow', 'strength', 'keep'],
  wealth: ['income', 'leak', 'path', 'months', 'now'],
  career: ['style', 'recognition', 'timing', 'try'],
  business: ['model', 'profit', 'timing', 'pitfall'],
  love: ['attraction', 'pattern', 'timing', 'caution'],
  marriage: ['pattern', 'scenes', 'timing', 'keep'],
  friends: ['role', 'types', 'timing', 'friction'],
  network: ['helpers', 'grow', 'timing', 'boundary'],
  condition: ['charge', 'signal', 'rest', 'rhythm'],
  clash: ['types', 'scenes', 'timing', 'harmony'],
} as const satisfies Record<Exclude<AiSection, 'monthly' | 'daeun'>, readonly string[]>
export type StandardSection = keyof typeof SECTION_PARTS
export const MONTH_COUNT = 12

export type StatusBucket = 'single' | 'dating' | 'married' | 'other'
export const STATUS_BUCKETS: readonly StatusBucket[] = ['single', 'dating', 'married', 'other']

export interface DaeunItem {
  ganZhi: string
  /** 만 나이 */
  startAge: number
}

export interface ReportInput {
  locale: Locale
  pillars: ChartPillars
  daeun: DaeunItem[]
  /** 기기 현지 오늘 YYYY-MM-DD. 월별 흐름의 시작 달과 지금 대운을 정한다. */
  today: string
  /** 오늘 걸린 대운 순번 (0부터). 첫 대운 전이면 -1. 기기에서 계산해 보낸다 (나이·생년은 보내지 않는다). */
  currentDaeun: number
  status: StatusBucket
  /**
   * 지금 나이의 5년 구간 시작 나이 (만 27세 → 25). **서버가** `results.birth_date` 로 채운다.
   * 기기가 보내는 값이 아니라 `readReportInput` 이 떨어뜨린다 — 나이를 기기가 정하면 안 된다.
   * 생년월일이 없으면 undefined 로 두고 예전처럼 대운 시작 나이(10년 폭)로 가늠한다.
   */
  ageBand?: AgeBand
  /**
   * 사주 주인의 성별. **서버가** 결과에 저장된 값으로 채운다 (`resultSex`) — 기기가 보내는 값은 떨어뜨린다.
   * 지시문의 성별 줄과 캐시 열쇠에 쓴다 (남녀가 같은 캐시 글을 받지 않게).
   */
  sex?: 'female' | 'male' | null
  /** 연애 상태 다섯 값 (`status` 가 묶은 복잡·말 안 함을 가른다). **서버가** 결과에 저장된 값으로 채운다 — 모든 장의 상태 줄과 캐시 열쇠 */
  relationship?: RelationshipStatus | null
  /** 요즘 가장 궁금한 것 (입력의 고민). **서버가** 결과에 저장된 값으로 채운다 — 리포트 재료와 캐시 열쇠에만 */
  concern?: Concern | null
  /** 띠 지지 (설날 기준 — 화면의 띠와 같다). **서버가** 생년월일로 채운다. 없으면 연주 지지 */
  zodiac?: string | null
}

/** 섹션 결과. 일반 섹션은 SECTION_PARTS, 월별은 m1~m12, 대운은 d1~dN 키. */
export type SectionParts = Record<string, string>

export function readReportInput(value: unknown): ReportInput | null {
  const v = value as Partial<Record<keyof ReportInput, unknown>> | null
  if (!v || typeof v !== 'object') return null
  if (!LOCALES.includes(v.locale as Locale)) return null
  const p = v.pillars as Partial<ChartPillars> | undefined
  if (!p || !isDayPillar(p.year) || !isDayPillar(p.month) || !isDayPillar(p.day)) return null
  if (p.hour !== null && !isDayPillar(p.hour)) return null
  if (!Array.isArray(v.daeun) || v.daeun.length > 12) return null
  const daeun: DaeunItem[] = []
  for (const d of v.daeun as Partial<DaeunItem>[]) {
    if (!d || !isDayPillar(d.ganZhi) || !Number.isInteger(d.startAge) || (d.startAge as number) < 0 || (d.startAge as number) > 130) return null
    daeun.push({ ganZhi: d.ganZhi, startAge: d.startAge as number })
  }
  if (typeof v.today !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v.today)) return null
  const year = Number(v.today.slice(0, 4))
  if (year < 2000 || year > 2100) return null
  if (!STATUS_BUCKETS.includes(v.status as StatusBucket)) return null
  if (!Number.isInteger(v.currentDaeun) || (v.currentDaeun as number) < -1 || (v.currentDaeun as number) >= daeun.length) return null
  return {
    locale: v.locale as Locale,
    pillars: { year: p.year, month: p.month, day: p.day, hour: p.hour ?? null },
    daeun,
    today: v.today,
    currentDaeun: v.currentDaeun as number,
    status: v.status as StatusBucket,
  }
}

export interface MonthAhead {
  year: number
  /** 달력 달 1~12 */
  month: number
  ganZhi: string
  score: number
  relation: Relation
  /** 이미 시작된 달인가 (첫 달이 오늘이 든 달일 때만) — 아니면 "지금부터" 가 아니라 달 이름으로 쓴다 */
  started?: boolean
}

/**
 * 기준 달(`baseMonth` — 달 끝 7일 안이면 다음 달)부터 앞으로 12개월. 달 간지는 그 달 초 절입 뒤의 월주다 (예: 10월 = 寒露 뒤 월주).
 * 화면에는 "10월" 로 쓰고, 절기 기준이라는 안내만 작게 붙인다.
 */
export function monthsAhead(dayPillar: string, today: string, count = MONTH_COUNT): MonthAhead[] {
  const base = baseMonth(today)
  const started = baseMonthStarted(today)
  let year = Number(base.slice(0, 4))
  let month = Number(base.slice(5, 7))
  const out: MonthAhead[] = []
  for (let i = 0; i < count; i += 1) {
    const ganZhi = month === 1 ? monthPillarsOfYear(year - 1)[11]!.ganZhi : monthPillarsOfYear(year)[month - 2]!.ganZhi
    out.push({ year, month, ganZhi, ...periodScore(dayPillar, ganZhi), started: i === 0 && started })
    month += 1
    if (month === 13) {
      month = 1
      year += 1
    }
  }
  return out
}

/**
 * **만 20살 아래인가.** 서버가 결과의 생년월일로 채운 5년 구간(`ageBand`)으로만 본다 —
 * 5년 구간이 20 아래면 만 나이도 20 아래다. 생년월일을 모르면(구간이 없으면) 어른과 같이 쓴다.
 * AI 에는 이 갈래("20살 아래")와 원래 보내던 5년 구간 이름만 간다 — 생년월일은 가지 않는다.
 */
export function isUnder20(input: Pick<ReportInput, 'ageBand'>): boolean {
  return isYouthAge(input.ageBand)
}

/**
 * 결혼 장을 **어떤 처지로** 쓰나 — 기혼은 지금 그대로, 연애 중은 "지금 관계가 함께 사는 쪽으로 간다면",
 * 싱글·모름은 "앞으로 함께 산다면(가정)", 20살 아래(기혼 아님)는 "앞으로 만날 인연·관계 스타일(가정)".
 */
export type MarriageFrame = 'married' | 'dating' | 'single' | 'youth'
export function marriageFrame(input: Pick<ReportInput, 'status' | 'ageBand'>): MarriageFrame {
  if (isUnder20(input)) return 'youth'
  if (isMarried(input.status)) return 'married'
  return input.status === 'dating' ? 'dating' : 'single'
}

export function isStructured(section: AiSection): 'monthly' | 'daeun' | 'standard' {
  return section === 'monthly' ? 'monthly' : section === 'daeun' ? 'daeun' : 'standard'
}

/** 섹션이 기대하는 구역 이름 */
export function partKeys(section: AiSection, input: Pick<ReportInput, 'daeun'>): string[] {
  switch (isStructured(section)) {
    case 'monthly':
      return Array.from({ length: MONTH_COUNT }, (_, i) => `m${i + 1}`)
    case 'daeun':
      return input.daeun.map((_, i) => `d${i + 1}`)
    default:
      return [...SECTION_PARTS[section as StandardSection]]
  }
}

/**
 * 기준 달이 글을 바꾸는 섹션. 이 섹션들만 캐시 키에 달을 넣는다.
 *
 * 예전에는 평생운세만 빼고 전부 달을 넣었는데, 애정·결혼·대운은 달이 바뀌어도 글이 그대로다
 * (재료에 달이 안 들어간다 — tests/cacheKey.test.ts 가 이걸 검사한다).
 * 그런데도 키가 달마다 갈라져서 같은 사주가 달이 바뀔 때마다 새로 만들어졌다 (대운 적중률 38%).
 */
// 애정·결혼도 — 재료의 시기 구간이 기준 달부터 열두 달이라 달마다 다르다. 열쇠에 달이 없으면 지난달 글(지난 날짜)이 그대로 나온다
export const MONTH_KEYED: readonly AiSection[] = ['wealth', 'career', 'business', 'love', 'marriage', 'friends', 'network', 'monthly', 'condition', 'clash']

/**
 * 섹션 캐시 키. 섹션마다 실제로 글에 쓰는 재료만 모아 해시한다 — 기준 달은 달을 실제로 쓰는 섹션에만.
 * 연애 상태(다섯 값)·고민·띠·성별·나이대는 모든 장의 지시에 들어가므로 모든 장의 열쇠에 든다.
 */
export async function reportCacheKey(section: AiSection, input: ReportInput): Promise<string> {
  const material: unknown[] = [section, input.pillars, input.ageBand ?? null]
  // 기준 달 + 그 달이 시작했는가 — 시작한 달의 글은 "지금부터" 로 쓰고, 달 끝 7일에 쓴 글은 그 달을 앞으로 올 달로 쓴다
  if (MONTH_KEYED.includes(section)) material.push(baseMonthStarted(input.today) ? baseMonth(input.today) : `${baseMonth(input.today)}|ahead`)
  if (section === 'daeun') material.push(input.daeun, input.currentDaeun)
  else material.push(input.daeun[input.currentDaeun]?.ganZhi ?? null)
  material.push(input.relationship ?? input.status, input.concern ?? null, input.zodiac ?? null, input.sex ?? null)
  return `report|${REPORT_VERSION}|${section}|${(await sha256Hex(JSON.stringify(material))).slice(0, 40)}|${input.locale}`
}
